"use client";

/**
 * FAQ Accordion Component
 *
 * Reusable FAQ section with accordion functionality.
 * Uses cream background card styling with premium animations.
 */
import { c as _c } from "react/compiler-runtime";
import { T } from "gt-next";
import { useFAQAccordion } from "../hooks/useInteractivity";
import { DEFAULT_FAQ_ITEMS } from "./faq-items";
import { getTranslatedFaqContent } from "./faq-translations";
import type { FAQItem } from "./faq-items";
export type { FAQItem };
export { DEFAULT_FAQ_ITEMS };
interface FAQProps {
  items?: FAQItem[];
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  /** Compact mode for modal/embedded contexts - smaller typography, tighter spacing, no card styling */
  compact?: boolean;
  /** When true, skip rendering the JSON-LD FAQPage schema (use when page already provides its own) */
  hideSchema?: boolean;
  /** Page-local translated nodes keyed by the exact English question (checked before the shared table). */
  translations?: Record<string, {
    question: React.ReactNode;
    answer: React.ReactNode;
  }>;
}

/**
 * AccordionContent - Premium height animation using CSS Grid technique
 *
 * Best-in-class approach for animating height to/from auto:
 * - grid-template-rows: 0fr → 1fr enables smooth height transitions
 * - Staggered opacity + subtle translateY creates premium feel
 * - Asymmetric timing: slower open (feels intentional), faster close (feels responsive)
 * - Custom easing: expo-out on open, ease-in on close (matches Apple/Linear/Stripe)
 * - Compact mode uses faster animations for modal context
 */
function AccordionContent(t0) {
  const $ = _c(13);
  const {
    isOpen,
    children,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const openDuration = compact ? "300ms" : "500ms";
  const closeDuration = compact ? "200ms" : "350ms";
  const contentOpenDuration = compact ? "250ms" : "400ms";
  const contentCloseDuration = compact ? "150ms" : "200ms";
  const t2 = isOpen ? "1fr" : "0fr";
  const t3 = isOpen ? `grid-template-rows ${openDuration} cubic-bezier(0.16, 1, 0.3, 1)` : `grid-template-rows ${closeDuration} cubic-bezier(0.4, 0, 0.6, 1)`;
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = {
      gridTemplateRows: t2,
      transition: t3
    };
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = isOpen ? 1 : 0;
  const t6 = isOpen ? "translateY(0)" : "translateY(-8px)";
  const t7 = isOpen ? `opacity ${contentOpenDuration} cubic-bezier(0.16, 1, 0.3, 1) 50ms, transform ${contentOpenDuration} cubic-bezier(0.16, 1, 0.3, 1) 50ms` : `opacity ${contentCloseDuration} cubic-bezier(0.4, 0, 1, 1), transform ${contentCloseDuration} cubic-bezier(0.4, 0, 1, 1)`;
  let t8;
  if ($[3] !== t5 || $[4] !== t6 || $[5] !== t7) {
    t8 = {
      opacity: t5,
      transform: t6,
      transition: t7
    };
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== children || $[8] !== t8) {
    t9 = <div className="overflow-hidden"><div style={t8}>{children}</div></div>;
    $[7] = children;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t4 || $[11] !== t9) {
    t10 = <div className="grid overflow-hidden" style={t4}>{t9}</div>;
    $[10] = t4;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  return t10;
}
export default function FAQ(t0) {
  const $ = _c(62);
  const {
    items: t1,
    eyebrow,
    title,
    compact: t2,
    hideSchema: t3,
    translations
  } = t0;
  const items = t1 === undefined ? DEFAULT_FAQ_ITEMS : t1;
  const compact = t2 === undefined ? false : t2;
  const hideSchema = t3 === undefined ? false : t3;
  const {
    toggle,
    isOpen
  } = useFAQAccordion();
  let t4;
  if ($[0] !== hideSchema || $[1] !== items) {
    t4 = !hideSchema && items.length > 0 ? JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": items.map(_temp)
    }).replace(/</g, "\\u003c") : null;
    $[0] = hideSchema;
    $[1] = items;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const faqSchema = t4;
  const iconTransitionOpen = compact ? "transform 250ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms cubic-bezier(0.4, 0, 1, 1) 50ms" : "transform 400ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms cubic-bezier(0.4, 0, 1, 1) 100ms";
  const iconTransitionClose = compact ? "transform 200ms cubic-bezier(0.4, 0, 0.6, 1), opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 50ms" : "transform 300ms cubic-bezier(0.4, 0, 0.6, 1), opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 100ms";
  if (compact) {
    let t5;
    if ($[3] !== faqSchema || $[4] !== hideSchema) {
      t5 = !hideSchema && faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: faqSchema
      }} />;
      $[3] = faqSchema;
      $[4] = hideSchema;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <h3 id="faq-heading-compact" className="text-[22px] font-bold tracking-tight text-black md:text-[28px]"><T>Frequently asked Questions</T></h3>;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <a href="https://support.outfame.com/en/" target="_blank" rel="noopener noreferrer" className="text-black/60 underline underline-offset-2 transition-colors hover:text-black">Help Center</a>;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    let t8;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="mx-auto mb-8 max-w-[480px] text-center lg:mb-10">{t6}<p className="mt-3 text-[14px] leading-[1.6] text-black/50 md:text-[15px] md:leading-[1.7]"><T>If you don't find what you're looking for – visit our{" "}{t7}, reach out via Intercom above, or email us at{" "}<a href="mailto:support@outfame.com" className="text-black/60 underline underline-offset-2 transition-colors hover:text-black">support@outfame.com</a></T></p></div>;
      $[8] = t8;
    } else {
      t8 = $[8];
    }
    let t9;
    if ($[9] !== iconTransitionClose || $[10] !== iconTransitionOpen || $[11] !== isOpen || $[12] !== items || $[13] !== toggle || $[14] !== translations) {
      let t10;
      if ($[16] !== iconTransitionClose || $[17] !== iconTransitionOpen || $[18] !== isOpen || $[19] !== toggle || $[20] !== translations) {
        t10 = (item_0, index) => {
          const itemIsOpen = isOpen(index);
          const translatedFaq = translations?.[item_0.question] ?? getTranslatedFaqContent(item_0);
          return <div key={index} className="rounded-xl bg-gray-50"><button className="group flex w-full items-center justify-between rounded-xl px-4 py-4 text-left transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20 focus-visible:ring-offset-1 md:px-5" onClick={() => toggle(index)} aria-expanded={itemIsOpen}><span className="pr-4 text-[15px] font-medium leading-[22px] text-black md:text-[16px] md:leading-[24px]">{translatedFaq?.question ?? item_0.question}</span><span className="flex h-5 w-5 shrink-0 items-center justify-center text-black/40" aria-hidden="true"><span className="relative h-3 w-3"><span className="absolute left-0 top-1/2 h-[1.5px] w-full rounded-full bg-current" style={{
                    transform: "translateY(-50%)"
                  }} /><span className="absolute left-0 top-1/2 h-[1.5px] w-full origin-center rounded-full bg-current" style={{
                    transform: itemIsOpen ? "translateY(-50%) rotate(0deg)" : "translateY(-50%) rotate(90deg)",
                    opacity: itemIsOpen ? 0 : 1,
                    transition: itemIsOpen ? iconTransitionOpen : iconTransitionClose
                  }} /></span></span></button><AccordionContent isOpen={itemIsOpen} compact={true}>{translatedFaq?.answer ? <div className="px-4 pb-4 text-[14px] leading-[21px] text-black/60 md:px-5 md:text-[15px] md:leading-[22px]">{translatedFaq.answer}</div> : <div className="px-4 pb-4 text-[14px] leading-[21px] text-black/60 md:px-5 md:text-[15px] md:leading-[22px]" dangerouslySetInnerHTML={{
                __html: item_0.answer
              }} />}</AccordionContent></div>;
        };
        $[16] = iconTransitionClose;
        $[17] = iconTransitionOpen;
        $[18] = isOpen;
        $[19] = toggle;
        $[20] = translations;
        $[21] = t10;
      } else {
        t10 = $[21];
      }
      t9 = items.map(t10);
      $[9] = iconTransitionClose;
      $[10] = iconTransitionOpen;
      $[11] = isOpen;
      $[12] = items;
      $[13] = toggle;
      $[14] = translations;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[22] !== t9) {
      t10 = <section className="pt-8 pb-4 lg:pt-12" aria-labelledby="faq-heading-compact">{t8}<div className="mx-auto flex max-w-[600px] flex-col gap-3">{t9}</div></section>;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== t10 || $[25] !== t5) {
      t11 = <>{t5}{t10}</>;
      $[24] = t10;
      $[25] = t5;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  let t5;
  if ($[27] !== faqSchema || $[28] !== hideSchema) {
    t5 = !hideSchema && faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: faqSchema
    }} />;
    $[27] = faqSchema;
    $[28] = hideSchema;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== eyebrow) {
    t6 = eyebrow ?? <T>FAQ</T>;
    $[30] = eyebrow;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== t6) {
    t7 = <p className="mb-4 text-[15px] font-semibold uppercase leading-[20px] text-black/50">{t6}</p>;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== title) {
    t8 = title ?? <T>Frequently asked questions</T>;
    $[34] = title;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t8) {
    t9 = <h2 id="faq-heading" className="faq-title font-mosans text-[36px] font-normal uppercase leading-none text-black md:text-[56px] md:leading-[0.95] lg:text-[72px]">{t8}</h2>;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t7 || $[39] !== t9) {
    t10 = <div className="mx-auto mb-8 max-w-[770px] text-center md:mb-16">{t7}{t9}</div>;
    $[38] = t7;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== iconTransitionClose || $[42] !== iconTransitionOpen || $[43] !== isOpen || $[44] !== items || $[45] !== toggle || $[46] !== translations) {
    let t12;
    if ($[48] !== iconTransitionClose || $[49] !== iconTransitionOpen || $[50] !== isOpen || $[51] !== toggle || $[52] !== translations) {
      t12 = (item_1, index_0) => {
        const itemIsOpen_0 = isOpen(index_0);
        const translatedFaq_0 = translations?.[item_1.question] ?? getTranslatedFaqContent(item_1);
        return <div key={index_0} className="border-t border-black/10"><button className="group flex w-full items-center justify-between py-4 text-left md:py-6" onClick={() => toggle(index_0)} aria-expanded={itemIsOpen_0}><span className="faq-question pr-4 text-[17px] font-semibold leading-[26px] text-black md:text-[20px] md:leading-[30px]">{translatedFaq_0?.question ?? item_1.question}</span><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black/5 md:h-9 md:w-9" aria-hidden="true"><span className="relative h-3.5 w-3.5 md:h-4 md:w-4"><span className="absolute left-0 top-1/2 h-[2px] w-full rounded-full bg-current" style={{
                  transform: "translateY(-50%)"
                }} /><span className="absolute left-0 top-1/2 h-[2px] w-full origin-center rounded-full bg-current" style={{
                  transform: itemIsOpen_0 ? "translateY(-50%) rotate(0deg)" : "translateY(-50%) rotate(90deg)",
                  opacity: itemIsOpen_0 ? 0 : 1,
                  transition: itemIsOpen_0 ? iconTransitionOpen : iconTransitionClose
                }} /></span></span></button><AccordionContent isOpen={itemIsOpen_0}>{translatedFaq_0?.answer ? <div className="faq-answer pb-4 text-[17px] leading-[26px] text-black/75 md:pb-6 md:text-[16px] md:leading-[24px]">{translatedFaq_0.answer}</div> : <div className="faq-answer pb-4 text-[17px] leading-[26px] text-black/75 md:pb-6 md:text-[16px] md:leading-[24px]" dangerouslySetInnerHTML={{
              __html: item_1.answer
            }} />}</AccordionContent></div>;
      };
      $[48] = iconTransitionClose;
      $[49] = iconTransitionOpen;
      $[50] = isOpen;
      $[51] = toggle;
      $[52] = translations;
      $[53] = t12;
    } else {
      t12 = $[53];
    }
    t11 = items.map(t12);
    $[41] = iconTransitionClose;
    $[42] = iconTransitionOpen;
    $[43] = isOpen;
    $[44] = items;
    $[45] = toggle;
    $[46] = translations;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[54] !== t11) {
    t12 = <div>{t11}</div>;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] !== t10 || $[57] !== t12) {
    t13 = <section className="bg-white px-3 pb-3" aria-labelledby="faq-heading"><div className="faq-container rounded-card bg-cream px-5 py-16 md:px-10 md:py-24 lg:px-16 lg:py-[120px]"><div className="mx-auto max-w-container">{t10}{t12}</div></div></section>;
    $[56] = t10;
    $[57] = t12;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  let t14;
  if ($[59] !== t13 || $[60] !== t5) {
    t14 = <>{t5}{t13}</>;
    $[59] = t13;
    $[60] = t5;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  return t14;
}
function _temp(item) {
  return {
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer.replace(/<[^>]*>/g, "")
    }
  };
}